import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Input,
  Badge,
  Skeleton,
  ErrorState,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Alert,
  AlertDescription,
} from '@/components/ui'
import { Key, Copy, RefreshCw, Eye, EyeOff, Plus, Trash2, AlertTriangle, Check } from 'lucide-react'
import { api, type AppFullInfo } from '@/api/client'

export function AppOAuthPage() {
  const { orgSlug, appId } = useParams()
  const [app, setApp] = useState<AppFullInfo | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showClientId, setShowClientId] = useState(false)
  const [isRotating, setIsRotating] = useState(false)
  const [newRedirectUri, setNewRedirectUri] = useState('')
  const [newSecret, setNewSecret] = useState<string | null>(null)
  const [secretCopied, setSecretCopied] = useState(false)

  useEffect(() => {
    const fetchApp = async () => {
      if (!orgSlug || !appId) return
      try {
        setError(null)
        const response = await api.apps.get(orgSlug, appId)
        setApp(response.app)
      } catch (err) {
        console.error('Failed to fetch app:', err)
        setError(err instanceof Error ? err.message : 'Failed to load OAuth settings')
      } finally {
        setIsLoading(false)
      }
    }
    fetchApp()
  }, [orgSlug, appId])

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
  }

  const copySecretToClipboard = () => {
    if (newSecret) {
      navigator.clipboard.writeText(newSecret)
      setSecretCopied(true)
      setTimeout(() => setSecretCopied(false), 2000)
    }
  }

  const handleRotateSecret = async () => {
    if (!orgSlug || !appId) return
    setIsRotating(true)
    try {
      const response = await api.apps.rotateSecret(orgSlug, appId)
      setNewSecret(response.client_secret)
      setSecretCopied(false)
    } catch (err) {
      console.error('Failed to rotate secret:', err)
    } finally {
      setIsRotating(false)
    }
  }

  const handleCloseSecretDialog = () => {
    setNewSecret(null)
    setSecretCopied(false)
  }

  const handleAddRedirectUri = async () => {
    if (!orgSlug || !appId || !app || !newRedirectUri) return
    try {
      const response = await api.apps.update(orgSlug, appId, {
        redirect_uris: [...app.redirect_uris, newRedirectUri],
      })
      setApp(response.app)
      setNewRedirectUri('')
    } catch (err) {
      console.error('Failed to add redirect URI:', err)
    }
  }

  const handleRemoveRedirectUri = async (uri: string) => {
    if (!orgSlug || !appId || !app) return
    try {
      const response = await api.apps.update(orgSlug, appId, {
        redirect_uris: app.redirect_uris.filter(u => u !== uri),
      })
      setApp(response.app)
    } catch (err) {
      console.error('Failed to remove redirect URI:', err)
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-6 max-w-2xl">
        <Card>
          <CardHeader><Skeleton className="h-5 w-32" /></CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-10 w-full" />
            </div>
            <div className="space-y-2">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-10 w-full" />
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (error || !app) {
    return (
      <ErrorState
        title="Failed to load OAuth settings"
        description={error || 'App not found'}
        action={<Button onClick={() => window.location.reload()}>Try Again</Button>}
      />
    )
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <Card>
        <CardHeader>
          <CardTitle>Client Credentials</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-[var(--text-primary)]">Client ID</label>
            <div className="flex items-center gap-2">
              <Input
                value={showClientId ? app.client_id : '•'.repeat(32)}
                readOnly
                className="font-mono"
              />
              <Button variant="ghost" size="sm" onClick={() => setShowClientId(!showClientId)}>
                {showClientId ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => copyToClipboard(app.client_id)}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-[var(--text-primary)]">Client Secret</label>
            <div className="flex items-center gap-2">
              <Input
                value="••••••••••••••••••••••••••••••••"
                readOnly
                className="font-mono"
              />
              <Button
                variant="secondary"
                size="sm"
                onClick={handleRotateSecret}
                disabled={isRotating}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${isRotating ? 'animate-spin' : ''}`} />
                Rotate
              </Button>
            </div>
            <p className="text-xs text-[var(--text-muted)]">
              The client secret is only shown once when created or rotated.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Redirect URIs</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-[var(--text-secondary)]">
            Allowed callback URLs for OAuth flows. Must be exact matches including query parameters.
          </p>

          {app.redirect_uris.length > 0 ? (
            <div className="space-y-2">
              {app.redirect_uris.map((uri) => (
                <div key={uri} className="flex items-center gap-2 p-2 bg-[var(--bg-elevated)] rounded-lg">
                  <code className="text-sm text-[var(--text-primary)] flex-1 truncate">{uri}</code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveRedirectUri(uri)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[var(--text-muted)] italic">No redirect URIs configured</p>
          )}

          <div className="flex items-center gap-2">
            <Input
              placeholder="https://example.com/auth/callback"
              value={newRedirectUri}
              onChange={(e) => setNewRedirectUri(e.target.value)}
            />
            <Button onClick={handleAddRedirectUri} disabled={!newRedirectUri}>
              <Plus className="h-4 w-4 mr-2" />
              Add
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>OAuth Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[var(--text-primary)]">Auto-authorize</p>
              <p className="text-sm text-[var(--text-muted)]">Skip consent screen for first-party apps</p>
            </div>
            <Badge variant={app.auto_authorize ? 'success' : 'default'}>
              {app.auto_authorize ? 'Enabled' : 'Disabled'}
            </Badge>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[var(--text-primary)]">Require consent</p>
              <p className="text-sm text-[var(--text-muted)]">Always show consent screen on authorization</p>
            </div>
            <Badge variant={app.require_consent ? 'success' : 'default'}>
              {app.require_consent ? 'Enabled' : 'Disabled'}
            </Badge>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[var(--text-primary)]">Allow PKCE Plain</p>
              <p className="text-sm text-[var(--text-muted)]">Allow plain code challenge method (less secure)</p>
            </div>
            <Badge variant={app.allow_pkce_plain ? 'warning' : 'success'}>
              {app.allow_pkce_plain ? 'Allowed' : 'S256 Only'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* New Secret Dialog */}
      <Dialog open={!!newSecret} onOpenChange={(open) => !open && handleCloseSecretDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Client Secret</DialogTitle>
            <DialogDescription>
              Your client secret has been rotated. Copy it now — it won't be shown again.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <Alert variant="warning">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Store this secret securely. You won't be able to see it again after closing this dialog.
              </AlertDescription>
            </Alert>

            <div className="flex items-center gap-2">
              <Input
                value={newSecret || ''}
                readOnly
                className="font-mono text-sm"
              />
              <Button
                variant="secondary"
                size="sm"
                onClick={copySecretToClipboard}
                className="shrink-0"
              >
                {secretCopied ? (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4 mr-2" />
                    Copy
                  </>
                )}
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={handleCloseSecretDialog}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
